import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@resy/ui-kitchen';
import Logger from '../../../../../../ui-kitchen/src/utils/logger';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import { getRetinaImage, handleMktAnalytics } from '../AmexUtils';
import { CTA_EVENT_NAME } from '../constants';
import './AmexFlipComponent.scss';

const AmexFlipComponent = (
  {
    awsFolderName,
    baseRequestUrl,
    flipConfig: {
      subsections = [],
    },
    Analytics,
    matchMedia,
  }
) => {
  if (subsections.length === 0) return null;
  const [finalSubsections, setFinalSubsections] = useState([]);
  const requiredKeys = ['title', 'copy', 'images'];
  const requiredImageKeys = ['desktop', 'mobile'];
  const errMsg = '{keyName} field in AmexFlipComponent is null or undefined or empty. Please fix and proceed.';

  useEffect(() => {
    const filteredSubsections = subsections
      .filter((item) => requiredKeys.every((i) => {
        const hasAllKeys = Object.prototype.hasOwnProperty.call(item, i) && item[i];
        return hasAllKeys || Logger.error(errMsg.replace('{keyName}', i));
      }))
      .filter((item) => {
        const { images } = item;
        const hasImage = requiredImageKeys
          .every((i) => {
            const hasAllImageKeys = Object.prototype.hasOwnProperty.call((images), i) && Object.keys(images[i]).length > 0;
            return hasAllImageKeys || Logger.error(errMsg.replace('{keyName}', i));
          });
        if (hasImage) return item;
        return null;
      });
    setFinalSubsections(filteredSubsections);
  }, [subsections]);

  if (finalSubsections.length === 0) return null;
  const isDesktop = matchMedia.lg;

  const displayCTA = (cta) => {
    const { aria, label, url } = cta;
    if (Object.keys(cta).length === 0 || !label || !url) return null;
    return (
      <Link
        className="AmexFlipComponent__section--cta cta"
        href={url}
        target="_blank"
        key={label}
        aria-label={aria}
        onClick={() => {
          const extraParams = {
            action_name: `${label} Click`,
          };
          handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraParams);
        }}
      >
        { label }
      </Link>
    );
  };

  const displayImage = (image, alt) => {
    if (isDesktop) {
      return (
        <div
          role="img"
          aria-label={alt}
          className="AmexFlipComponent__section--image"
          style={{ backgroundImage: `url(${image})` }}
        />
      );
    }
    return (
      <img
        className="AmexFlipComponent__section--image-mobile"
        src={image}
        alt={alt}
      />
    );
  };

  const displayFlipSections = (subsection) => {
    const {
      title,
      copy,
      fine_copy: fineCopy = null,
      image_position: imagePosition,
      image_alt: alt = '',
      cta = {},
      images: {
        desktop: desktopImg,
        mobile: mobileImg,
      },
    } = subsection;
    const urlPrefix = `${baseRequestUrl}${awsFolderName}/images/flipImages/`;
    const imageName = getRetinaImage(desktopImg, mobileImg, isDesktop);
    const image = `${urlPrefix}${imageName}`;
    const sanitizedTitle = sanitizeString(title);
    const sanitizedCopy = sanitizeString(copy);
    const sanitizedFineCopy = sanitizeString(fineCopy);

    return (
      <div
        key={copy}
        className={`AmexFlipComponent__section image-${imagePosition}`}
      >
        <div className="AmexFlipComponent__section--content">
          <div className="AmexFlipComponent__section--wrapper">
            <h2
              className="AmexFlipComponent__section--title"
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizedTitle,
              }}
            />
            <p
              className="AmexFlipComponent__section--copy"
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizedCopy,
              }}
            />
            {fineCopy && (
            <p
              className="AmexFlipComponent__section--fineCopy"
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizedFineCopy,
              }}
            />
            )}
            {displayCTA(cta)}
          </div>
        </div>
        {displayImage(image, alt)}
      </div>
    );
  };

  return (
    <section className="AmexFlipComponent">
      <div className="container">
        <div className="AmexFlipComponent__sections">
          {finalSubsections.map((subsection) => displayFlipSections(subsection))}
        </div>
      </div>
    </section>
  );
};

AmexFlipComponent.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  flipConfig: PropTypes.shape({
    subsections: PropTypes.arrayOf(PropTypes.shape({
      copy: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      fine_copy: PropTypes.string,
      image_position: PropTypes.string,
      image_alt: PropTypes.string,
      images: PropTypes.shape({
        desktop: PropTypes.shape({}).isRequired,
        mobile: PropTypes.shape({}).isRequired,
      }).isRequired,
      cta: PropTypes.shape({
        label: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        aria: PropTypes.string,
        action_name: PropTypes.string,
      }),
    })).isRequired,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
};

export default AmexFlipComponent;
